import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LayoutPage from "../../Components/LayoutPage";
import SVG from "../../Components/SVG";
import { useTicketCount } from "../../Context/TicketCountContext";
import { API_BASE_URL } from "../../config/api";
import { ButtonLoader, EmptyState, TableLoadingState } from "../../Components/AsyncState";
import Pagination from "../../Components/Pagination/Pagination";
import { observeInfiniteScroll } from "../../helper/observeInfiniteScroll";

const REFRESH_ENDPOINT = `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`;
const SUPPORT_BASE_PATH = `${API_BASE_URL}/api/v0/support`;
const PRIORITY_TICKETS_ENDPOINT = `${SUPPORT_BASE_PATH}/admin/tickets/priority/`;
const OTHER_TICKETS_ENDPOINT = `${SUPPORT_BASE_PATH}/admin/tickets/other/`;

const COMPRESSIBLE_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
]);
const MAX_COMPRESS_DIMENSION = 1600;
const JPEG_COMPRESS_QUALITY = 0.78;

const sortByUpdatedAtDesc = (items = []) => {
  return [...items].sort((a, b) => {
    const aTime = new Date(a?.updated_at || 0).getTime();
    const bTime = new Date(b?.updated_at || 0).getTime();
    return bTime - aTime;
  });
};

const sortByCreatedAtAsc = (items = []) => {
  return [...items].sort((a, b) => {
    const aTime = new Date(a?.created_at || 0).getTime();
    const bTime = new Date(b?.created_at || 0).getTime();
    return aTime - bTime;
  });
};

const normalizeTicketListPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const mergeTicketsByUuid = (previousTickets = [], nextTickets = []) => {
  const ticketsMap = new Map();

  previousTickets.forEach((ticket) => {
    ticketsMap.set(ticket?.ticket_uuid, ticket);
  });

  nextTickets.forEach((ticket) => {
    ticketsMap.set(ticket?.ticket_uuid, ticket);
  });

  return Array.from(ticketsMap.values());
};

const buildOtherTicketsUrl = (page = 1) => {
  const params = new URLSearchParams();
  params.set("page", String(page));
  return `${OTHER_TICKETS_ENDPOINT}?${params.toString()}`;
};

const getErrorMessageFromPayload = (payload, fallbackText) => {
  if (!payload) return fallbackText;
  if (typeof payload.detail === "string" && payload.detail.trim()) {
    return payload.detail;
  }

  const firstArrayValue = Object.values(payload).find(
    (value) => Array.isArray(value) && value.length > 0,
  );
  if (firstArrayValue && typeof firstArrayValue[0] === "string") {
    return firstArrayValue[0];
  }

  return fallbackText;
};

const canvasToBlob = (canvas, type, quality) => {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
};

const loadImageFromFile = (file) => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ img, objectUrl });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("image-load-failed"));
    };
    img.src = objectUrl;
  });
};

const compressImageFile = async (file) => {
  if (!COMPRESSIBLE_IMAGE_TYPES.has(String(file?.type || "").toLowerCase())) {
    return file;
  }

  try {
    const { img, objectUrl } = await loadImageFromFile(file);
    const width = Number(img.naturalWidth || img.width || 0);
    const height = Number(img.naturalHeight || img.height || 0);

    if (width <= 0 || height <= 0) {
      URL.revokeObjectURL(objectUrl);
      return file;
    }

    const largestSide = Math.max(width, height);
    const scale =
      largestSide > MAX_COMPRESS_DIMENSION
        ? MAX_COMPRESS_DIMENSION / largestSide
        : 1;

    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext("2d");

    if (!context) {
      URL.revokeObjectURL(objectUrl);
      return file;
    }

    context.drawImage(img, 0, 0, targetWidth, targetHeight);
    URL.revokeObjectURL(objectUrl);

    const outputType =
      file.type === "image/png" || file.type === "image/webp"
        ? file.type
        : "image/jpeg";
    const quality =
      outputType === "image/png" ? undefined : JPEG_COMPRESS_QUALITY;

    const blob = await canvasToBlob(canvas, outputType, quality);
    if (!blob || blob.size >= file.size) {
      return file;
    }

    const baseName = file.name.replace(/\.[^/.]+$/, "");
    const nextExtension =
      outputType === "image/png"
        ? "png"
        : outputType === "image/webp"
          ? "webp"
          : "jpg";
    const nextName = `${baseName}.${nextExtension}`;

    return new File([blob], nextName, {
      type: outputType,
      lastModified: Date.now(),
    });
  } catch (error) {
    console.error("Image compression failed:", error);
    return file;
  }
};

const compressImageFiles = async (files = []) => {
  return Promise.all(files.map((file) => compressImageFile(file)));
};

const LoadingState = ({ more = false }) => <TableLoadingState more={more} />;

export default function Support() {
  const navigate = useNavigate();
  const { fetchTicketCount } = useTicketCount();

  const [activeTab, setActiveTab] = useState("priority");
  const [priorityTickets, setPriorityTickets] = useState([]);
  const [priorityTicketsLoadingMore, setPriorityTicketsLoadingMore] = useState(false);
  const [priorityTicketsPagination, setPriorityTicketsPagination] = useState({
    page: 1,
    count: 0,
    pageSize: 20,
    totalPages: 0,
  });
  const [otherTickets, setOtherTickets] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [otherTicketsLoadingMore, setOtherTicketsLoadingMore] = useState(false);
  const [listError, setListError] = useState("");
  const [otherTicketsPagination, setOtherTicketsPagination] = useState({
    page: 1,
    count: 0,
    pageSize: 0,
    totalPages: 1,
  });

  const [selectedTicketUuid, setSelectedTicketUuid] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const [chatText, setChatText] = useState("");
  const [chatDocuments, setChatDocuments] = useState([]);
  const [chatError, setChatError] = useState("");
  const [chatSubmitting, setChatSubmitting] = useState(false);

  const markReadInProgressRef = useRef(new Set());
  const otherTicketsRequestIdRef = useRef(0);
  const otherTicketsListRef = useRef(null);
  const otherTicketsLoadMoreRef = useRef(null);
  const otherTicketsPaginationRef = useRef({
    page: 1,
    count: 0,
    pageSize: 0,
    totalPages: 1,
  });

  const logout = useCallback(() => {
    localStorage.removeItem("access");
    localStorage.removeItem("authCredentials");
    localStorage.removeItem("refresh");
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    navigate("/signin");
  }, [navigate]);

  const authFetch = useCallback(
    async (url, options = {}) => {
      const headers = new Headers(options.headers || {});
      if (!(options.body instanceof FormData) && !headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }
      headers.set("Authorization", `Bearer ${localStorage.getItem("access")}`);

      let response = await fetch(url, {
        ...options,
        headers,
      });

      if (response.status !== 401) return response;

      const refreshResponse = await fetch(REFRESH_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refresh: localStorage.getItem("refresh"),
        }),
      });

      if (!refreshResponse.ok) {
        logout();
        return null;
      }

      const refreshData = await refreshResponse.json();
      localStorage.setItem("access", refreshData.access);

      const retryHeaders = new Headers(options.headers || {});
      if (
        !(options.body instanceof FormData) &&
        !retryHeaders.has("Content-Type")
      ) {
        retryHeaders.set("Content-Type", "application/json");
      }
      retryHeaders.set(
        "Authorization",
        `Bearer ${localStorage.getItem("access")}`,
      );

      response = await fetch(url, {
        ...options,
        headers: retryHeaders,
      });

      return response;
    },
    [logout],
  );

  const fetchTicketLists = useCallback(
    async ({ withLoading = true } = {}) => {
      if (withLoading) setListLoading(true);
      setListError("");
      const requestId = otherTicketsRequestIdRef.current + 1;
      otherTicketsRequestIdRef.current = requestId;

      try {
        const loadedOtherPages = 1;

        const [priorityResponse, ...otherResponses] = await Promise.all([
          authFetch(`${PRIORITY_TICKETS_ENDPOINT}?page=1`, {
            method: "GET",
          }),
          ...Array.from({ length: loadedOtherPages }, (_, index) =>
            authFetch(buildOtherTicketsUrl(index + 1), {
              method: "GET",
            }),
          ),
        ]);

        if (!priorityResponse || otherResponses.some((response) => !response)) {
          return;
        }

        if (
          !priorityResponse.ok ||
          otherResponses.some((response) => !response.ok)
        ) {
          throw new Error("load-ticket-lists-failed");
        }

        const [priorityPayload, ...otherPayloads] = await Promise.all([
          priorityResponse.json(),
          ...otherResponses.map((response) => response.json()),
        ]);

        setPriorityTickets(
          sortByUpdatedAtDesc(normalizeTicketListPayload(priorityPayload)),
        );
        setPriorityTicketsPagination({
          page: priorityPayload.page ?? 1,
          count: priorityPayload.count ?? 0,
          pageSize: priorityPayload.page_size ?? 20,
          totalPages: priorityPayload.total_pages ?? 0,
        });

        const mergedOtherTickets = sortByUpdatedAtDesc(
          otherPayloads.flatMap((payload) =>
            normalizeTicketListPayload(payload),
          ),
        );
        const lastOtherPayload = otherPayloads[otherPayloads.length - 1] || {};

        if (requestId !== otherTicketsRequestIdRef.current) {
          return;
        }

        setOtherTickets(mergedOtherTickets);
        const nextPagination = {
          page: lastOtherPayload.page ?? loadedOtherPages,
          count: lastOtherPayload.count ?? mergedOtherTickets.length,
          pageSize:
            lastOtherPayload.page_size ?? lastOtherPayload.pageSize ?? 0,
          totalPages:
            lastOtherPayload.total_pages ??
            lastOtherPayload.totalPages ??
            loadedOtherPages,
        };
        otherTicketsPaginationRef.current = nextPagination;
        setOtherTicketsPagination(nextPagination);
      } catch (error) {
        console.error("Error fetching support ticket lists:", error);
        setListError("بارگذاری لیست تیکت‌ها با خطا مواجه شد.");
      } finally {
        if (requestId === otherTicketsRequestIdRef.current) {
          setOtherTicketsLoadingMore(false);
        }
        if (withLoading) setListLoading(false);
      }
    },
    [authFetch],
  );

  const fetchMoreOtherTickets = useCallback(async () => {
    if (
      listLoading ||
      otherTicketsLoadingMore ||
      otherTicketsPagination.page >= otherTicketsPagination.totalPages
    ) {
      return;
    }

    const nextPage = otherTicketsPagination.page + 1;
    const requestId = otherTicketsRequestIdRef.current + 1;
    otherTicketsRequestIdRef.current = requestId;
    setOtherTicketsLoadingMore(true);

    try {
      const response = await authFetch(buildOtherTicketsUrl(nextPage), {
        method: "GET",
      });

      if (!response) return;
      if (!response.ok) {
        throw new Error("load-more-other-tickets-failed");
      }

      const payload = await response.json();
      if (requestId !== otherTicketsRequestIdRef.current) {
        return;
      }

      const nextTickets = normalizeTicketListPayload(payload);

      setOtherTickets((previousTickets) =>
        sortByUpdatedAtDesc(mergeTicketsByUuid(previousTickets, nextTickets)),
      );
      const nextPaginationState = {
        page: payload.page ?? nextPage,
        count: payload.count ?? otherTicketsPagination.count,
        pageSize: payload.page_size ?? otherTicketsPagination.pageSize,
        totalPages:
          payload.total_pages ?? otherTicketsPagination.totalPages ?? nextPage,
      };
      otherTicketsPaginationRef.current = nextPaginationState;
      setOtherTicketsPagination(nextPaginationState);
    } catch (error) {
      console.error("Error loading more support tickets:", error);
      setListError("بارگذاری لیست تیکت‌ها با خطا مواجه شد.");
    } finally {
      if (requestId === otherTicketsRequestIdRef.current) {
        setOtherTicketsLoadingMore(false);
      }
    }
  }, [
    authFetch,
    listLoading,
    otherTicketsLoadingMore,
    otherTicketsPagination.count,
    otherTicketsPagination.page,
    otherTicketsPagination.pageSize,
    otherTicketsPagination.totalPages,
  ]);

  const fetchMorePriorityTickets = useCallback(async (nextPage) => {
    if (priorityTicketsLoadingMore) return;
    setPriorityTicketsLoadingMore(true);
    try {
      const response = await authFetch(
        `${PRIORITY_TICKETS_ENDPOINT}?page=${Math.max(1, nextPage)}`,
        { method: "GET" },
      );
      if (!response || !response.ok) throw new Error("load-more-priority-tickets-failed");
      const payload = await response.json();
      const nextTickets = normalizeTicketListPayload(payload);
      setPriorityTickets((current) =>
        sortByUpdatedAtDesc(mergeTicketsByUuid(current, nextTickets)),
      );
      setPriorityTicketsPagination({
        page: payload.page ?? nextPage,
        count: payload.count ?? priorityTicketsPagination.count,
        pageSize: payload.page_size ?? 20,
        totalPages: payload.total_pages ?? priorityTicketsPagination.totalPages,
      });
    } catch (error) {
      console.error("Error loading more priority tickets:", error);
      setListError("بارگذاری لیست تیکت‌ها با خطا مواجه شد.");
    } finally {
      setPriorityTicketsLoadingMore(false);
    }
  }, [authFetch, priorityTicketsLoadingMore, priorityTicketsPagination.count, priorityTicketsPagination.totalPages]);

  const fetchTicketDetail = useCallback(
    async (ticketUuid) => {
      if (!ticketUuid) return;

      setDetailLoading(true);
      setDetailError("");
      try {
        const response = await authFetch(
          `${SUPPORT_BASE_PATH}/tickets/${ticketUuid}/`,
          {
            method: "GET",
          },
        );

        if (!response) return;
        if (!response.ok) {
          throw new Error("load-ticket-detail-failed");
        }

        const payload = await response.json();
        const sortedChats = sortByCreatedAtAsc(
          Array.isArray(payload?.chats) ? payload.chats : [],
        );

        setSelectedTicket({
          ...payload,
          chats: sortedChats,
        });
      } catch (error) {
        console.error("Error fetching support ticket detail:", error);
        setDetailError("بارگذاری جزئیات گفتگو با خطا مواجه شد.");
        setSelectedTicket(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [authFetch],
  );

  const markTicketAsRead = useCallback(
    async (ticketUuid) => {
      if (!ticketUuid) return;
      if (markReadInProgressRef.current.has(ticketUuid)) return;

      markReadInProgressRef.current.add(ticketUuid);
      try {
        const response = await authFetch(
          `${SUPPORT_BASE_PATH}/tickets/${ticketUuid}/mark-read/`,
          {
            method: "POST",
          },
        );

        if (!response || !response.ok) return;
        const clearUnread = (tickets) =>
          tickets.map((ticket) =>
            ticket.ticket_uuid === ticketUuid
              ? { ...ticket, unread_count_for_me: 0 }
              : ticket,
          );
        setPriorityTickets(clearUnread);
        setOtherTickets(clearUnread);
      } catch (error) {
        console.error("Error marking support ticket as read:", error);
      } finally {
        markReadInProgressRef.current.delete(ticketUuid);
      }
    },
    [authFetch],
  );

  useEffect(() => {
    fetchTicketLists();
  }, [fetchTicketLists]);

  useEffect(() => {
    const root = otherTicketsListRef.current;
    const target = otherTicketsLoadMoreRef.current;

    if (!root || !target || activeTab !== "other") {
      return;
    }

    return observeInfiniteScroll(target, fetchMoreOtherTickets, { root });
  }, [activeTab, fetchMoreOtherTickets, otherTickets.length]);

  const activeTickets = useMemo(() => {
    return activeTab === "priority" ? priorityTickets : otherTickets;
  }, [activeTab, priorityTickets, otherTickets]);

  const allTicketsMap = useMemo(() => {
    const merged = [...priorityTickets, ...otherTickets];
    return new Map(merged.map((ticket) => [ticket.ticket_uuid, ticket]));
  }, [priorityTickets, otherTickets]);

  useEffect(() => {
    if (listLoading) return;
    if (activeTickets.length === 0) {
      setSelectedTicketUuid(null);
      return;
    }

    const isSelectedInCurrentTab = activeTickets.some(
      (ticket) => ticket.ticket_uuid === selectedTicketUuid,
    );

    if (!isSelectedInCurrentTab) {
      setSelectedTicketUuid(activeTickets[0].ticket_uuid);
    }
  }, [activeTickets, listLoading, selectedTicketUuid]);

  useEffect(() => {
    if (!selectedTicketUuid) {
      setSelectedTicket(null);
      setDetailError("");
      setChatText("");
      setChatDocuments([]);
      setChatError("");
      return;
    }

    fetchTicketDetail(selectedTicketUuid);
  }, [fetchTicketDetail, selectedTicketUuid]);

  useEffect(() => {
    if (!selectedTicketUuid) return;
    const selectedFromList = allTicketsMap.get(selectedTicketUuid);
    if (!selectedFromList) return;
    if ((selectedFromList?.unread_count_for_me || 0) <= 0) return;

    markTicketAsRead(selectedTicketUuid);
  }, [selectedTicketUuid, allTicketsMap, markTicketAsRead]);

  const appendImagesToChatDocs = (files = []) => {
    const incoming = Array.from(files);
    if (incoming.length === 0) return;

    const imageFiles = incoming.filter((file) =>
      String(file?.type || "").startsWith("image/"),
    );

    if (imageFiles.length !== incoming.length) {
      setChatError("فقط فایل تصویری مجاز است.");
    }

    if (imageFiles.length > 0) {
      setChatDocuments((prev) => [...prev, ...imageFiles]);
    }
  };

  const handleSendChat = async (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (!selectedTicketUuid) return;

    const text = chatText.trim();
    if (!text && chatDocuments.length === 0) {
      setChatError("حداقل یکی از فیلدهای پیام یا تصویر را وارد کنید.");
      return;
    }

    setChatSubmitting(true);
    setChatError("");
    try {
      const requestOptions = {
        method: "POST",
      };

      if (chatDocuments.length > 0) {
        const formData = new FormData();
        if (text) formData.append("text", text);
        const compressedDocuments = await compressImageFiles(chatDocuments);
        compressedDocuments.forEach((file) => {
          formData.append("documents", file);
        });
        requestOptions.body = formData;
      } else {
        requestOptions.body = JSON.stringify({ text });
      }

      const response = await authFetch(
        `${SUPPORT_BASE_PATH}/tickets/${selectedTicketUuid}/chats/`,
        requestOptions,
      );

      if (!response) return;
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        setChatError(
          getErrorMessageFromPayload(payload, "ارسال پیام با خطا مواجه شد."),
        );
        return;
      }

      setChatText("");
      setChatDocuments([]);
      const refreshRequests = [
        fetchTicketDetail(selectedTicketUuid),
      ];

      if (activeTab === "priority") {
        refreshRequests.push(fetchTicketCount());
      }

      await Promise.all(refreshRequests);
    } catch (error) {
      console.error("Error sending support chat message:", error);
      setChatError("ارسال پیام با خطا مواجه شد.");
    } finally {
      setChatSubmitting(false);
    }
  };

  const selectedFromList = selectedTicketUuid
    ? allTicketsMap.get(selectedTicketUuid)
    : null;
  const selectedTitle = selectedTicket?.title || selectedFromList?.title || "";
  const selectedRelatedAccountCode =
    selectedTicket?.related_account_code ||
    selectedFromList?.related_account_code ||
    "";

  return (
    <LayoutPage
      activeIndex={7}
      navItems={["پشتیبانی مشتریان"]}
      childrenClassName="flex-1 min-h-0 flex flex-col gap-2.5"
    >
      <div
        dir="rtl"
        className="w-full flex-1 min-h-0 flex flex-col lg:flex-row gap-2.5"
      >
        <div className="order-2 flex-1 min-h-0 rounded-2xl border border-zi-blue-light bg-zi-bg2-light dark:bg-zi-bg2-dark text-right">
          <div className="w-full h-full min-h-0 p-3 sm:p-5 flex flex-col gap-4">
            {selectedTicketUuid ? (
              <>
                <div className="w-full border-b border-zi-border-light dark:border-zi-border-dark pb-3">
                  <p className="font-medium text-zi-text-light dark:text-zi-text-dark">
                    {selectedTitle}
                  </p>
                  {selectedRelatedAccountCode && (
                    <p className="text-xs mt-1 text-zi-text-light/70 dark:text-zi-text-dark/70">
                      حساب مرتبط: {selectedRelatedAccountCode}
                    </p>
                  )}
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4 px-1">
                  {detailLoading && <LoadingState />}

                  {!detailLoading && detailError && (
                    <p className="text-red-500 text-sm">{detailError}</p>
                  )}

                  {!detailLoading &&
                    !detailError &&
                    selectedTicket &&
                    selectedTicket.chats.length === 0 && <EmptyState />}

                  {!detailLoading &&
                    !detailError &&
                    selectedTicket &&
                    selectedTicket.chats.map((chat) => {
                      const isAdmin = chat?.sender_role === "admin";

                      return (
                        <div
                          key={chat?.id}
                          className={`max-w-[85%] md:max-w-[70%] rounded-2xl p-3 flex flex-col gap-2 border ${
                            isAdmin
                              ? "ml-auto bg-[#d9e8f1] border-[#c5d8e6] dark:bg-[#2b4f68] dark:border-[#3d6f8f]"
                              : "mr-auto bg-[#ececec] border-[#d9d9d9] dark:bg-[#2a2f37] dark:border-[#3b4452]"
                          }`}
                        >
                          <p className="text-xs text-[#4b5563] dark:text-[#b7cadb]">
                            {isAdmin
                              ? chat?.sender_username || "شما"
                              : chat?.sender_username || "کاربر"}
                          </p>
                          {chat?.text && (
                            <p className="text-sm sm:text-base text-[#111827] dark:text-[#e7f2ff] break-words leading-7">
                              {chat.text}
                            </p>
                          )}
                          {Array.isArray(chat?.documents) &&
                            chat.documents.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {chat.documents.map((doc) => (
                                  <a
                                    key={doc?.id}
                                    href={doc?.image}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="rounded-md overflow-hidden border border-zi-border-light dark:border-zi-border-dark bg-zi-bg1-light dark:bg-zi-bg1-dark p-1 inline-flex"
                                  >
                                    <img
                                      src={doc?.image}
                                      alt="support-document"
                                      className="max-w-[220px] max-h-44 w-auto h-auto object-contain rounded"
                                    />
                                  </a>
                                ))}
                              </div>
                            )}
                        </div>
                      );
                    })}
                </div>

                <form
                  onSubmit={handleSendChat}
                  className="w-full border-t border-zi-border-light dark:border-zi-border-dark pt-3 flex flex-col gap-3"
                >
                  {chatDocuments.length > 0 && (
                    <div className="w-full flex flex-wrap gap-2">
                      {chatDocuments.map((file, index) => (
                        <div
                          key={`${file.name}-${index}`}
                          className="flex items-center gap-2 rounded-md px-2 py-1 bg-zi-bg1-light dark:bg-zi-bg1-dark border border-zi-border-light dark:border-zi-border-dark"
                        >
                          <p className="text-xs text-zi-text-light dark:text-zi-text-dark">
                            {file.name}
                          </p>
                          <button
                            type="button"
                            onClick={() =>
                              setChatDocuments((prev) =>
                                prev.filter((_, i) => i !== index),
                              )
                            }
                            className="text-red-500 text-xs"
                          >
                            حذف
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="w-full flex items-center gap-2">
                    <input
                      type="text"
                      value={chatText}
                      onChange={(event) => {
                        setChatText(event.target.value);
                        if (chatError) setChatError("");
                      }}
                      placeholder="پیام خود را اینجا بنویسید"
                      className="flex-1 min-w-0 rounded-lg border border-zi-border-light dark:border-zi-border-dark bg-zi-bg1-light dark:bg-zi-bg1-dark px-3 py-2 text-sm text-zi-text-light dark:text-zi-text-dark outline-none hocus:ring-1 hocus:ring-zi-border-light dark:hocus:ring-zi-border-dark"
                    />

                    <label
                      htmlFor="support-chat-upload"
                      className="size-10 rounded-lg border border-zi-border-light dark:border-zi-border-dark flex items-center justify-center cursor-pointer bg-zi-bg1-light dark:bg-zi-bg1-dark"
                    >
                      <SVG
                        name="upload"
                        classname="size-4 stroke-zi-blue-light dark:stroke-zi-blue-light"
                      />
                    </label>
                    <input
                      id="support-chat-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(event) => {
                        appendImagesToChatDocs(event.target.files);
                        event.target.value = "";
                      }}
                    />

                    <button
                      type="submit"
                      disabled={chatSubmitting}
                      className="rounded-lg px-4 py-2 text-sm text-white bg-zi-gradient-button-blue disabled:opacity-50"
                    >
                      {chatSubmitting ? <ButtonLoader label="در حال ارسال پیام" /> : "ارسال"}
                    </button>
                  </div>

                  {chatError && (
                    <p className="text-red-500 text-sm">{chatError}</p>
                  )}
                </form>
              </>
            ) : (
              <EmptyState message="یک تیکت را برای مشاهده گفتگو انتخاب کنید." />
            )}
          </div>
        </div>

        <div className="order-1 w-full lg:w-[360px] min-h-0 rounded-2xl border border-zi-blue-light bg-zi-bg2-light dark:bg-zi-bg2-dark p-3 sm:p-4 flex flex-col gap-3">
          <div className="w-full p-1 rounded-lg border border-zi-border-light dark:border-zi-border-dark bg-zi-bg1-light dark:bg-zi-bg1-dark flex items-center gap-1">
            <button
              type="button"
              onClick={() => setActiveTab("priority")}
              className={`flex-1 rounded-md py-2 text-sm ${
                activeTab === "priority"
                  ? "bg-zi-bg2-light dark:bg-zi-bg2-dark text-zi-blue-light"
                  : "text-zi-text-light/60 dark:text-zi-text-dark/60"
              }`}
            >
              فعال
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("other")}
              className={`flex-1 rounded-md py-2 text-sm ${
                activeTab === "other"
                  ? "bg-zi-bg2-light dark:bg-zi-bg2-dark text-zi-blue-light"
                  : "text-zi-text-light/60 dark:text-zi-text-dark/60"
              }`}
            >
              پایان‌یافته
            </button>
          </div>

          <div
            ref={otherTicketsListRef}
            className="w-full flex-1 min-h-0 overflow-y-auto flex flex-col gap-2"
          >
            {listLoading && <LoadingState />}

            {!listLoading && listError && (
              <p className="text-red-500 text-sm">{listError}</p>
            )}

            {!listLoading && !listError && activeTickets.length === 0 && (
              <EmptyState />
            )}

            {!listLoading &&
              !listError &&
              activeTickets.map((ticket) => {
                const isActive = ticket?.ticket_uuid === selectedTicketUuid;
                return (
                  <button
                    key={ticket?.ticket_uuid}
                    type="button"
                    onClick={() => setSelectedTicketUuid(ticket?.ticket_uuid)}
                    className={`w-full rounded-lg p-3 flex flex-col gap-1.5 text-right border ${
                      isActive
                        ? "border-zi-blue-light bg-zi-bg1-light dark:bg-zi-bg1-dark"
                        : "border-zi-border-light dark:border-zi-border-dark hocus:border-zi-blue-light"
                    }`}
                  >
                    <div className="w-full flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-zi-text-light dark:text-zi-text-dark truncate">
                        {ticket?.title || "-"}
                      </p>
                      {ticket?.unread_count_for_me > 0 && (
                        <span className="size-2.5 rounded-full bg-zi-radial-gradient-blue" />
                      )}
                    </div>

                    {ticket?.related_account_code && (
                      <p className="text-xs text-zi-text-light/60 dark:text-zi-text-dark/60 truncate">
                        حساب مرتبط: {ticket.related_account_code}
                      </p>
                    )}

                    <p className="text-xs text-zi-text-light/60 dark:text-zi-text-dark/60 truncate">
                      {ticket?.last_chat?.text || "بدون پیام"}
                    </p>
                  </button>
                );
              })}

            {activeTab === "other" && !listLoading && !listError && (
              <div
                ref={otherTicketsLoadMoreRef}
                className="w-full py-3 text-center text-sm text-zi-text-light/60 dark:text-zi-text-dark/60"
              >
                {otherTicketsLoadingMore ? <LoadingState more /> : ""}
              </div>
            )}
            {activeTab === "priority" && !listLoading && !listError && (
              <Pagination
                page={priorityTicketsPagination.page}
                totalPages={priorityTicketsPagination.totalPages}
                loading={priorityTicketsLoadingMore}
                onPageChange={fetchMorePriorityTickets}
                scrollRootRef={otherTicketsListRef}
              />
            )}
          </div>
        </div>
      </div>
    </LayoutPage>
  );
}
